import { useState } from "react";
import { useTranslations } from "next-intl";
import { PConfirmDialog } from "@/components/PConfirmDialog/PConfirmDialog";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { AcceptanceState } from "@/types/enums/AcceptanceState";

type RedecideDialogProps = {
  /**
   * Main-guest request or companion request.
   *
   * Not decoration: the copy names the automation that will (or will not) run,
   * and the two lanes fire DIFFERENT triggers — `REQUEST_APPROVED` vs
   * `COMPANION_APPROVED`, which the automation editor lists under visibly
   * different names. Naming the wrong one sends the admin looking for a rule
   * that is not there.
   */
  registrant: boolean;
  /** What the request is being changed TO. */
  target: AcceptanceState;
  /**
   * What it is NOW. Two of the promises below depend on it, and getting them
   * from the target alone is how they came to be wrong.
   */
  current: AcceptanceState | null;
  isLoading: boolean;
  onConfirm: (retriggerAutomations: boolean) => void;
  onCancel: () => void;
};

/**
 * Confirm changing a decision that has already been made.
 *
 * Two things this dialog exists to fix, both of them honesty problems:
 *
 * 1. Re-deciding used to run NO automation while the cockpit reported success
 *    three separate ways (a toast, a "request approved" timeline row, and the
 *    flipped chip). The copy here says plainly that nothing is sent unless the
 *    box is ticked, and the box is the only thing that makes it true.
 * 2. Re-announcing a decision is not free and cannot be recalled: lifecycle
 *    triggers are transactional (an unsubscribed guest still gets the mail),
 *    there is no per-guest send de-duplication and no email quota on that path.
 *    So it is opt-in per action rather than a consequence of editing a row.
 *
 * The checkbox is HIDDEN when the target is PENDING: reopening fires nothing
 * server-side, so offering to "run the automations again" there would be the
 * same false promise in a new place.
 *
 * 3. The contingent. A guest slot is spent when someone becomes counted and is
 *    never given back — not by a decline, not by reopening, not even by
 *    deleting the guest. So reopening says plainly that the slot stays spent
 *    (nobody should reopen expecting a refund), and re-approving says that it
 *    may cost one, conditionally: the server charges at most once per guest for
 *    all time, and only it knows whether this one has already paid.
 */
export const RedecideDialog: React.FC<RedecideDialogProps> = ({
  registrant,
  target,
  current,
  isLoading,
  onConfirm,
  onCancel,
}) => {
  const t = useTranslations("eventGuestsRequests");
  const [retrigger, setRetrigger] = useState(false);

  const reopen = target === AcceptanceState.PENDING;
  const title = reopen ? t("reopenTitle") : t("redecideTitle");
  // The automation this decision would announce, named exactly as the
  // automation editor names it. The old copy said "the approval and denial
  // automations", which in German collided with the labels of GUEST_ACCEPTED /
  // GUEST_DECLINED — a different pair of triggers that this path never fires.
  const automation = t(
    target === AcceptanceState.ACCEPTED
      ? registrant
        ? "automationRequestApproved"
        : "automationCompanionApproved"
      : registrant
        ? "automationRequestDenied"
        : "automationCompanionDenied",
  );
  const message = reopen
    ? t("reopenBody")
    : target === AcceptanceState.ACCEPTED
      ? t("redecideAcceptBody", { automation })
      : t("redecideDeclineBody", { automation });
  const confirmText = reopen ? t("reopenConfirm") : t("redecideConfirm");
  // Waitlist membership is the capacity engine's, and the server announces
  // nothing for a decision that starts at SYSTEM — the opt-in cannot fire, so
  // offering it would promise an email that is never sent, and the toast would
  // then report "automations triggered" for a write that triggered none.
  const optInCanFire = current !== AcceptanceState.SYSTEM;

  // What it costs. Only a state that really spent a slot may claim one was
  // spent: a DECLINED request never became counted, so telling its reopener
  // that "the slot stays used" invents a charge that never happened.
  const costNote = reopen
    ? current === AcceptanceState.ACCEPTED
      ? t("reopenNoRefund")
      : null
    : target === AcceptanceState.ACCEPTED
      ? t("redecideAcceptCost")
      : null;

  return (
    <PConfirmDialog
      title={title}
      message={message}
      confirmText={confirmText}
      isLoading={isLoading}
      onConfirm={() => onConfirm(!reopen && optInCanFire && retrigger)}
      onCancel={onCancel}
    >
      {costNote && (
        <p className="body-4-regular font-secondary" data-testid="redecide-cost-note">
          {costNote}
        </p>
      )}
      {!reopen && optInCanFire && (
        <CheckboxWithValidation
          isChecked={retrigger}
          disabled={isLoading}
          size={18}
          // The hint wraps to two or three lines — without this the box drifts
          // to the middle of the text block instead of the first line.
          alignStart
          onCheckChangeAction={setRetrigger}
          testId="redecide-retrigger"
        >
          <span className="flex flex-col">
            <span className="body-3-regular">{t("retriggerLabel")}</span>
            <span className="body-4-regular font-secondary">
              {t("retriggerHint", { automation })}
            </span>
          </span>
        </CheckboxWithValidation>
      )}
    </PConfirmDialog>
  );
};
